import React, { useRef, useState } from 'react';
import { Link, useSearchParams, useLocation } from 'react-router-dom';
import './Contact.css';
import emailjs from 'emailjs-com'; // Make sure to install emailjs-com
import ReCAPTCHA from 'react-google-recaptcha';
import { normalizeContactReason, getContactPreset } from '../utils/contactIntent';
import PageHero from './PageHero';

const Contact = () => {
  const formRef = useRef();
  const leadFromStorageRef = useRef(null);
  const leadStorageConsumedRef = useRef(false);
  const [searchParams] = useSearchParams();
  const location = useLocation();
  const [status, setStatus] = useState('');
  const [recaptchaToken, setRecaptchaToken] = useState('');
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const reasonParam = searchParams.get('reason');
  const topicParam = searchParams.get('topic');
  const stateReason = location.state?.contactReason;
  const stateTopic = location.state?.contactTopic;

  // Prefill from ?reason=&topic=, navigate state, and/or home lead capture (localStorage)
  React.useEffect(() => {
    const savedLeadRaw = localStorage.getItem('leadFormData');
    if (savedLeadRaw && !leadStorageConsumedRef.current) {
      try {
        leadFromStorageRef.current = JSON.parse(savedLeadRaw);
        leadStorageConsumedRef.current = true;
        localStorage.removeItem('leadFormData');
      } catch (error) {
        console.error('Error parsing lead form data:', error);
      }
    }
    const leadData = leadFromStorageRef.current;

    const reasonKey = normalizeContactReason(reasonParam || stateReason);
    const rawTopic = topicParam || stateTopic || '';
    let topic = '';
    if (rawTopic) {
      try {
        topic = decodeURIComponent(String(rawTopic).trim());
      } catch {
        topic = String(rawTopic).trim();
      }
    }

    setFormData(prev => {
      const name = leadData?.name || prev.name || '';
      const email = leadData?.email || prev.email || '';

      let subject = '';
      let message = '';

      if (reasonKey) {
        const preset = getContactPreset(reasonKey);
        if (preset) {
          subject = preset.subject;
          message = preset.message;
        }
      } else if (leadData?.projectType) {
        subject = `Project inquiry: ${leadData.projectType}`;
        message = `I'm interested in: ${leadData.projectType}\n\nPlease share more about how ShawnyMedia can help.`;
      }

      if (topic) {
        message = message.trimEnd() + `\n\n— Regarding: ${topic}\n`;
      }

      if (leadData?.projectType && reasonKey) {
        message = message.trimEnd() + `\n\nFrom my note on your site: ${leadData.projectType}`;
      }

      return {
        name,
        email,
        subject: subject || prev.subject,
        message: message || prev.message
      };
    });

    const shouldScroll = Boolean(reasonKey || topic || leadData);
    if (shouldScroll) {
      const scrollTimer = window.setTimeout(() => {
        document.getElementById('contact-form')?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 120);
      return () => clearTimeout(scrollTimer);
    }
    return undefined;
  }, [reasonParam, topicParam, stateReason, stateTopic]);

  // Simple validation helpers
  const isValidEmail = (email) => /.+@.+\..+/.test(email);

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  // EmailJS handler with honeypot and validation
  const sendEmail = (e) => {
    e.preventDefault();
    setStatus('');
    const form = formRef.current;
    // Honeypot check
    if (form.website && form.website.value) {
      setStatus('Spam detected.');
      return;
    }
    // Simple validation
    const name = form.name.value.trim();
    const email = form.email.value.trim();
    const subject = form.subject.value.trim();
    const message = form.message.value.trim();
    if (!name || !email || !subject || !message) {
      setStatus('Please fill in all required fields.');
      return;
    }
    if (!isValidEmail(email)) {
      setStatus('Please enter a valid email address.');
      return;
    }
    if (message.length < 10) {
      setStatus('Message must be at least 10 characters.');
      return;
    }
    if (!recaptchaToken) {
      setStatus('Please complete the reCAPTCHA.');
      return;
    }
    setStatus('Sending...');
    emailjs.sendForm(
      'service_vf3zkq2', 
      'template_mgxj4gm', 
      formRef.current,
      'AGlcs9Px5S9iDW5bM' 
    )
      .then((result) => {
        setStatus('Message sent!');
        formRef.current.reset();
        setRecaptchaToken('');
        // Clear form data state
        setFormData({
          name: '',
          email: '',
          subject: '',
          message: ''
        });
      }, (error) => {
        setStatus('Failed to send message. Please try again later.');
      });
  };

  const isError = status && status !== 'Message sent!' && status !== 'Sending...';

  return (
    <>
      <PageHero
        eyebrow="Contact"
        title="Tell us what you're building."
        lede="Websites, web apps, quote flows, information systems — or a rough idea you haven't shaped yet. Send it over and we'll come back with next steps."
        meta={['Reply within 1 business day', 'Free 30-min consult']}
      >
        <a href="#contact-form" className="btn btn--primary">
          Send a message <span className="btn-arrow">→</span>
        </a>
        <a
          href="https://calendly.com/shawnysmedia/30min"
          target="_blank"
          rel="noopener noreferrer"
          className="btn btn--ghost"
        >
          Book a 30-min call
        </a>
      </PageHero>

      <main className="main-content" id="main">
        {/* ---- Channels ---------------------------------------------------- */}
        <section className="section contact-section">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow">Reach us</p>
              <h2 className="section-title reveal reveal-up">Pick whichever is easiest.</h2>
            </div>

            <ul className="contact-grid" data-stagger="0.06">
              <li className="contact-card reveal reveal-up">
                <h3>Email</h3>
                <a href="mailto:shawnysmedia@gmail.com" className="contact-link">
                  <img src="/mail.svg" alt="" />
                  <span>shawnysmedia@gmail.com</span>
                </a>
                <p>Business inquiries and project discussions.</p>
              </li>

              <li className="contact-card reveal reveal-up">
                <h3>Schedule</h3>
                <a
                  href="https://calendly.com/shawnysmedia/30min"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-link"
                >
                  <img src="/phone.svg" alt="" />
                  <span>Book a 30-min call</span>
                </a>
                <p>Pick a time on Calendly — free consultation, no pitch.</p>
              </li>

              <li className="contact-card reveal reveal-up">
                <h3>YouTube</h3>
                <a
                  href="https://www.youtube.com/@Shawny-Media"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-link"
                >
                  <img src="/yt.svg" alt="" />
                  <span>@Shawny-Media</span>
                </a>
                <p>Tutorials, demos, and build walkthroughs.</p>
              </li>

              <li className="contact-card reveal reveal-up">
                <h3>Instagram</h3>
                <a
                  href="https://www.instagram.com/shawny_media"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="contact-link"
                >
                  <img src="/ig.svg" alt="" />
                  <span>@shawny_media</span>
                </a>
                <p>Updates and behind-the-scenes.</p>
              </li>
            </ul>
          </div>
        </section>

        {/* ---- Form + sidebar ---------------------------------------------- */}
        <section className="section section--alt contact-form-section" id="contact-form">
          <div className="container contact-form-grid">
            <div className="contact-form-copy">
              <p className="eyebrow">The form</p>
              <h2 className="section-title reveal reveal-up">Ready to get started?</h2>
              <p className="section-lede reveal reveal-up" style={{ '--reveal-delay': '0.08s' }}>
                Describe your business, your timeline, and whether you need public pages,
                a full app, or both.
              </p>
              <p className="contact-prefill-note reveal reveal-fade">
                Subject and message pre-fill based on the button you arrived from. Edit
                anything before sending.
              </p>

              <div className="contact-capabilities reveal reveal-up">
                <h3>What we handle</h3>
                <ul>
                  <li>Local &amp; service business websites</li>
                  <li>Web apps and information systems</li>
                  <li>Frontend and backend engineering</li>
                  <li>Database design and optimization</li>
                  <li>UI/UX improvements</li>
                  <li>Social media marketing &amp; content</li>
                  <li>SEO and analytics</li>
                  <li>Graphic design, branding, illustration</li>
                </ul>
              </div>
            </div>

            <form
              ref={formRef}
              className="contact-form reveal reveal-scale"
              onSubmit={sendEmail}
              autoComplete="off"
            >
              {/* Honeypot field (hidden from users) */}
              <input
                type="text"
                name="website"
                style={{ display: 'none' }}
                tabIndex="-1"
                autoComplete="off"
              />

              <div className="form-row">
                <div className="form-group">
                  <label className="field-label" htmlFor="name">
                    Name *
                  </label>
                  <input
                    className="field"
                    type="text"
                    id="name"
                    name="name"
                    required
                    placeholder="Your full name"
                    value={formData.name}
                    onChange={handleInputChange}
                  />
                </div>
                <div className="form-group">
                  <label className="field-label" htmlFor="email">
                    Email *
                  </label>
                  <input
                    className="field"
                    type="email"
                    id="email"
                    name="email"
                    required
                    placeholder="your.email@example.com"
                    value={formData.email}
                    onChange={handleInputChange}
                  />
                </div>
              </div>

              <div className="form-group">
                <label className="field-label" htmlFor="subject">
                  Subject *
                </label>
                <input
                  className="field"
                  type="text"
                  id="subject"
                  name="subject"
                  required
                  placeholder="What's this about?"
                  value={formData.subject}
                  onChange={handleInputChange}
                />
              </div>

              <div className="form-group">
                <label className="field-label" htmlFor="message">
                  Message *
                </label>
                <textarea
                  className="field"
                  id="message"
                  name="message"
                  required
                  rows="6"
                  placeholder="e.g. Landscaping site with a quote form, a nonprofit event hub, or a custom dashboard…"
                  value={formData.message}
                  onChange={handleInputChange}
                />
              </div>

              <div className="contact-recaptcha">
                <ReCAPTCHA
                  sitekey="6LewK3MrAAAAAAmQpMVTBm0mTlqILi1PglrOwkmi"
                  theme="dark"
                  onChange={(token) => setRecaptchaToken(token)}
                />
              </div>

              <button type="submit" className="btn btn--primary btn--block btn--lg">
                Send message <span className="btn-arrow">→</span>
              </button>

              {status && (
                <p
                  className={`contact-status${isError ? ' contact-status--error' : ''}`}
                  role="status"
                >
                  {status}
                </p>
              )}
            </form>
          </div>
        </section>

        {/* ---- Live sites --------------------------------------------------- */}
        <section className="section contact-live-sites">
          <div className="container">
            <div className="section-head">
              <p className="eyebrow">Proof</p>
              <h2 className="section-title reveal reveal-up">Recent live sites.</h2>
              <p className="section-lede reveal reveal-up" style={{ '--reveal-delay': '0.08s' }}>
                Real client work you can open right now — the same builds featured on our{' '}
                <Link to="/projects">Projects</Link> page.
              </p>
            </div>

            <ul className="contact-live-sites-list" data-stagger="0.05">
              <li className="reveal reveal-up">
                <a href="https://www.majorcontent.com/" target="_blank" rel="noopener noreferrer">
                  majorcontent.com
                </a>
                <span>TV, film &amp; literary management company</span>
              </li>
              <li className="reveal reveal-up">
                <a href="https://bin-shine.netlify.app/" target="_blank" rel="noopener noreferrer">
                  BinShine
                </a>
                <span>Connecticut bin cleaning — 3D tour, booking, and customer accounts</span>
              </li>
              <li className="reveal reveal-up">
                <a href="https://proscapeny.netlify.app/" target="_blank" rel="noopener noreferrer">
                  ProScape
                </a>
                <span>Landscaping — home, services, contact, and quote requests</span>
              </li>
              <li className="reveal reveal-up">
                <a href="https://docreplay.netlify.app/" target="_blank" rel="noopener noreferrer">
                  DocReplay
                </a>
                <span>Document replayer — integrity and AI-awareness workflows</span>
              </li>
              <li className="reveal reveal-up">
                <a href="https://davisreel.com/" target="_blank" rel="noopener noreferrer">
                  davisreel.com
                </a>
                <span>Interactive 3D movie review platform</span>
              </li>
              <li className="reveal reveal-up">
                <a href="https://investnyou.org/" target="_blank" rel="noopener noreferrer">
                  investnyou.org
                </a>
                <span>Information &amp; investment tooling</span>
              </li>
              <li className="reveal reveal-up">
                <a href="https://luriecup.org/" target="_blank" rel="noopener noreferrer">
                  luriecup.org
                </a>
                <span>Charity soccer tournament benefiting hospital care</span>
              </li>
            </ul>
          </div>
        </section>
      </main>
    </>
  );
};

export default Contact; 